import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  PaulAdminApiService,
  PaulCoachLesson,
  PaulLessonStatus,
  PaulRunDetailResponse,
  PaulVerificationStatus,
} from '../../services/paul-admin/paul-admin-api.service';

@Component({
  selector: 'app-paul-coaching-detail',
  templateUrl: './paul-coaching-detail.component.html',
  styleUrls: ['./paul-coaching-detail.component.scss'],
})
export class PaulCoachingDetailComponent implements OnInit {
  isDark = localStorage.getItem('paul_admin_theme') !== 'light';
  loading = true;
  savingLesson = false;
  errorMessage = '';
  successMessage = '';
  detail: PaulRunDetailResponse | null = null;
  selectedLesson: PaulCoachLesson | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly paulApi: PaulAdminApiService,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const runUuid = this.route.snapshot.paramMap.get('coach_run_uuid') || '';
    if (!runUuid) {
      this.loading = false;
      this.errorMessage = 'The PAUL run ID is missing.';
      return;
    }
    this.loadRun(runUuid, this.route.snapshot.queryParamMap.get('lesson'));
  }

  get evaluation(): Record<string, any> | null {
    const value = this.detail?.run.response_payload?.['evaluation'];
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : null;
  }

  get gradeIssues(): any[] {
    const issues = this.evaluation?.['grade']?.['issues'];
    return Array.isArray(issues) ? issues : [];
  }

  get evaluationTargetLabel(): string {
    const target = this.evaluation?.['target'];
    if (!target || typeof target !== 'object' || Array.isArray(target)) {
      return '—';
    }
    return String(target['name'] || target['model'] || '—');
  }

  get evaluationCoachingNote(): string | null {
    const value = this.evaluation?.['coaching_note'];
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  chooseLesson(lesson: PaulCoachLesson): void {
    this.selectedLesson = lesson;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { lesson: lesson.lesson_uuid },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  updateDecision(status: PaulLessonStatus): void {
    this.updateLesson({ status });
  }

  updateVerification(status: PaulVerificationStatus): void {
    this.updateLesson({ verification_status: status });
  }

  backToGrid(): void {
    void this.router.navigate(['/paul/coaching']);
  }

  openChat(): void {
    void this.router.navigate(['/paul/chat']);
  }

  toggleTheme(): void {
    this.isDark = !this.isDark;
    localStorage.setItem('paul_admin_theme', this.isDark ? 'dark' : 'light');
  }

  pretty(value: unknown): string {
    return JSON.stringify(value ?? {}, null, 2);
  }

  humanize(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, character => character.toUpperCase());
  }

  formatDate(value: unknown): string {
    if (!value) {
      return '—';
    }
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  }

  executionLabel(model: string | null): string {
    if (model === 'paul-qwen36') {
      return 'GX10 · Qwen3.6 35B-A3B';
    }
    if (model === 'paul_api_fast_path') {
      return 'Rule-routed historical result · LLM was bypassed';
    }
    return model || 'Unknown execution path';
  }

  private loadRun(runUuid: string, preferredLessonUuid: string | null): void {
    this.loading = true;
    this.errorMessage = '';
    this.paulApi.getRun(runUuid).subscribe({
      next: response => {
        this.detail = response;
        this.selectedLesson = preferredLessonUuid
          ? response.lessons.find(lesson => lesson.lesson_uuid === preferredLessonUuid) || null
          : (response.lessons.length === 1 ? response.lessons[0] : null);
        this.loading = false;
        this.changeDetector.detectChanges();
      },
      error: error => {
        this.loading = false;
        this.errorMessage = this.errorText(error, 'The PAUL run details could not be loaded.');
        this.changeDetector.detectChanges();
      },
    });
  }

  private updateLesson(change: { status?: PaulLessonStatus; verification_status?: PaulVerificationStatus }): void {
    if (!this.selectedLesson || this.savingLesson) {
      return;
    }
    this.savingLesson = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.paulApi.updateLesson(this.selectedLesson.lesson_uuid, change).subscribe({
      next: response => {
        this.savingLesson = false;
        this.selectedLesson = response.lesson;
        if (this.detail) {
          this.detail.lessons = this.detail.lessons.map(lesson => lesson.lesson_uuid === response.lesson.lesson_uuid ? response.lesson : lesson);
        }
        this.successMessage = 'The coaching lesson was updated.';
        this.changeDetector.detectChanges();
      },
      error: error => {
        this.savingLesson = false;
        this.errorMessage = this.errorText(error, 'The coaching lesson could not be updated.');
        this.changeDetector.detectChanges();
      },
    });
  }

  private errorText(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const message = error.error?.message || error.error?.error || error.message;
      if (typeof message === 'string' && message.trim() !== '') {
        return message;
      }
    }
    return fallback;
  }
}
